import { adminApi } from '@/config/axios.config';

export interface PolicyPlanSummary {
  id: string;
  name?: string;
  status?: string;
  visibility?: string;
  billingCycle?: string;
}

export interface SubscriptionPolicy {
  id: string;
  name: string;
  description?: string;
  trialDays: number;
  trialEnabled: boolean;
  autoRenewal: boolean;
  gracePeriodDays: number;
  cancelationPolicy: 'immediate' | 'end_of_period' | 'custom';
  refundPolicy: 'no_refund' | 'partial_refund' | 'full_refund';
  maxTrialsPerUser: number;
  requiresPaymentMethod: boolean;
  allowDowngrade: boolean;
  allowUpgrade: boolean;
  downgradeTiming: 'immediate' | 'end_of_period';
  upgradeTiming: 'immediate' | 'end_of_period';
  status: 'active' | 'inactive' | 'draft';
  isDefault: boolean;
  applicablePlans?: PolicyPlanSummary[];
  settings?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPolicyFilters {
  status?: string;
  search?: string;
  trialEnabled?: boolean;
}

export interface SubscriptionPolicyStats {
  total: number;
  active: number;
  inactive: number;
  draft: number;
  withTrial: number;
  withAutoRenewal: number;
}

export interface CreateSubscriptionPolicyDto {
  name: string;
  description?: string;
  trialDays: number;
  trialEnabled: boolean;
  autoRenewal: boolean;
  gracePeriodDays: number;
  cancelationPolicy: 'immediate' | 'end_of_period' | 'custom';
  refundPolicy: 'no_refund' | 'partial_refund' | 'full_refund';
  maxTrialsPerUser: number;
  requiresPaymentMethod: boolean;
  allowDowngrade: boolean;
  allowUpgrade: boolean;
  downgradeTiming: 'immediate' | 'end_of_period';
  upgradeTiming: 'immediate' | 'end_of_period';
  status: 'active' | 'inactive' | 'draft';
  isDefault: boolean;
  applicablePlans?: string[];
  settings?: Record<string, any>;
}

export type UpdateSubscriptionPolicyDto = Partial<CreateSubscriptionPolicyDto>;

const normalizePolicy = (policy: any): SubscriptionPolicy => {
  if (!policy) {
    return policy;
  }

  const rawPlans = Array.isArray(policy.applicablePlans) ? policy.applicablePlans : [];
  
  const applicablePlans: PolicyPlanSummary[] = rawPlans.map((plan: any, index: number) => {
    if (typeof plan === 'string') {
      const normalized = { id: plan };
      return normalized;
    }

    const normalized = {
      id: plan.id,
      name: plan.name,
      status: plan.status,
      visibility: plan.visibility,
      billingCycle: plan.billingCycle,
    };
    return normalized;
  });

  const result = {
    ...policy,
    applicablePlans,
  };

  return result;
};

/**
 * API service para gestion de politicas de suscripcion
 */
export const subscriptionPoliciesApiService = {
  /**
   * Obtiene todas las politicas de suscripcion
   */
  getAllPolicies: async (filters?: SubscriptionPolicyFilters): Promise<SubscriptionPolicy[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.trialEnabled !== undefined) params.append('trialEnabled', filters.trialEnabled.toString());

      const response = await adminApi.get('/subscription-policies', { params });
      
      const data = response.data?.data ?? response.data;
      
      if (!Array.isArray(data)) {
        return [];
      }
      
      
      const normalized = data.map(normalizePolicy);

      return normalized;
    } catch (error) {
      // Retornar array vacio en caso de error
      return [];
    }
  },

  /**
   * Obtiene una politica por ID
   */
  getPolicy: async (id: string): Promise<SubscriptionPolicy> => {
    const response = await adminApi.get(`/subscription-policies/${id}`);
    const data = response.data?.data ?? response.data;
    return normalizePolicy(data);
  },

  /**
   * Obtiene las estadisticas de politicas
   */
  getPolicyStats: async (): Promise<SubscriptionPolicyStats> => {
    try {
      const response = await adminApi.get('/subscription-policies/stats');
      return response.data?.data ?? response.data;
    } catch (error) {
      // Retornar estadisticas por defecto en caso de error
      return {
        total: 0,
        active: 0,
        inactive: 0,
        draft: 0,
        withTrial: 0,
        withAutoRenewal: 0,
      };
    }
  },

  /**
   * Crea una nueva politica
   */
  createPolicy: async (policyData: CreateSubscriptionPolicyDto): Promise<SubscriptionPolicy> => {
    const response = await adminApi.post('/subscription-policies', policyData);
    const data = response.data?.data ?? response.data;
    return normalizePolicy(data);
  },

  /**
   * Actualiza una politica
   */
  updatePolicy: async (id: string, policyData: UpdateSubscriptionPolicyDto): Promise<SubscriptionPolicy> => {
    const response = await adminApi.patch(`/subscription-policies/${id}`, policyData);
    const data = response.data?.data ?? response.data;
    return normalizePolicy(data);
  },

  /**
   * Elimina una politica
   */
  deletePolicy: async (id: string): Promise<void> => {
    await adminApi.delete(`/subscription-policies/${id}`);
  },

  /**
   * Cambia el estado de una politica (toggle)
   */
  togglePolicyStatus: async (id: string): Promise<SubscriptionPolicy> => {
    const response = await adminApi.patch(`/subscription-policies/${id}/toggle-status`);
    const data = response.data?.data ?? response.data;
    return normalizePolicy(data);
  },

  /**
   * Duplica una politica
   */
  duplicatePolicy: async (id: string): Promise<SubscriptionPolicy> => {
    const response = await adminApi.post(`/subscription-policies/${id}/duplicate`);
    const data = response.data?.data ?? response.data;
    return normalizePolicy(data);
  },

  /**
   * Establece una politica como predeterminada
   */
  setAsDefault: async (id: string): Promise<SubscriptionPolicy> => {
    try {
      const response = await adminApi.patch(`/subscription-policies/${id}/set-default`);
      const data = response.data?.data ?? response.data;
      return normalizePolicy(data);
    } catch (error) {
      throw error;
    }
  },
};

export default subscriptionPoliciesApiService;
